// The pitch card's three-dot menu — the REAL, SILENT agent ban.
//
// Work order (TP_PITCH_LIFECYCLE.md, PART A, "The real ban", line 46):
//   "Today's 'Ban Agent' is a dummy at both ends (FE sends nothing, BE is a
//    print placeholder) — remove it end to end and build the REAL, SILENT ban:
//    the three-dot button becomes a real menu — 'Ban agent from this request'
//    and 'Ban agent for this transfer window' — with confirm dialogs and honest
//    copy. […] The agent is NEVER informed — painless, no drama."
//
// And line 47, the fact that is easiest to forget and therefore said out loud
// in BOTH dialogs:
//   "the ban affects VISIBILITY and NEW pitch creation only — existing pitches
//    of a banned agent (any state) continue their normal lifecycle untouched"
//
// So the club is told three things BEFORE it confirms, never after:
//   1. what stops   — the agent loses sight of the request(s), cannot pitch
//   2. what is sent — nothing; the agent is not notified
//   3. what carries on — proposals already in the list are untouched
//
// The wording deliberately echoes pages/club/BannedAgents.js (WP4a) so that
// banning here and unbanning there read as one feature: the sentence
// "Proposals they already sent you are not affected — they run their normal
// course." is that page's line 162 verbatim.
//
// This component owns only the menu and the confirm step. The call itself is
// `onBan(scope)` — it lives in useViewPitches beside accept / reject / scouting,
// because the club, staff and coach pitch pages share that one brain. Three
// copies of a ban dialog is exactly what the dummy did, and exactly how the
// copy of one of them drifts.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiDotsHorizontal } from "react-icons/hi";

import ConfirmationModal from "../club/ConfirmationModal";

export const BAN_SCOPE_REQUEST = "request";
export const BAN_SCOPE_TRANSFER_WINDOW = "transfer_window";

// One place for the two dialogs, so the request and the window variant cannot
// drift apart. `name` is already resolved by the caller (never a raw null).
export const banDialogCopy = (scope, name) => {
  const isWindow = scope === BAN_SCOPE_TRANSFER_WINDOW;
  return {
    headline: isWindow
      ? `Ban ${name} for this transfer window?`
      : `Ban ${name} from this request?`,
    effect: isWindow
      ? "They will no longer see any of your requests for the rest of this transfer window and cannot send new proposals to them."
      : "They will no longer see this request and cannot send new proposals to it.",
    silence: "They are not notified — nothing is sent to them.",
    // The point PART A line 47 makes, in the club's language.
    unaffected:
      "Proposals they already sent you are not affected — they run their normal course.",
    reversible: "You can lift the ban any time under Banned Agents.",
  };
};

const MENU_ITEMS = [
  { scope: BAN_SCOPE_REQUEST, label: "Ban agent from this request" },
  { scope: BAN_SCOPE_TRANSFER_WINDOW, label: "Ban agent for this transfer window" },
];

const BanAgentMenu = ({ pitch, onBan }) => {
  const [open, setOpen] = useState(false);
  const [pendingScope, setPendingScope] = useState(null);
  const [banning, setBanning] = useState(false);
  const rootRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  // Click-away and Escape, the two ways every other dropdown on the platform
  // closes. Bound only while the menu is open so a list of twenty cards does
  // not carry twenty idle listeners.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const agentName = pitch?.applied_by_agent?.name || "this agent";
  const copy = pendingScope ? banDialogCopy(pendingScope, agentName) : null;

  const handleConfirm = async () => {
    if (!pendingScope || banning) return;
    setBanning(true);
    const ok = await onBan(pitch, pendingScope);
    setBanning(false);
    // A failure keeps the dialog open with the honest toast behind it, so the
    // club can retry or cancel — closing it would read like the ban went
    // through.
    if (ok) setPendingScope(null);
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        title="More actions"
        className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F9FAFB]"
      >
        <HiDotsHorizontal className="w-5 h-5" />
      </button>

      {open && (
        // `right-0` anchors the panel to the button's RIGHT edge so it opens
        // leftwards. The width cap was `calc(100vw - 2.5rem)` = 280px, which at
        // a 320px viewport never bit (the panel is 272px) — measured, the panel
        // then hung 24px OFF the left edge of the screen and the first letter of
        // "Ban agent…" was unreachable (inventory 5.6). The cap is now
        // `calc(100vw - 2.25rem)`: the page's own 16px gutters plus the card's
        // 1px borders, which is what a 320px measurement showed the panel needs
        // to land fully inside the viewport (measured left = 3, right = 287).
        <div
          role="menu"
          aria-label="Agent actions"
          className="absolute right-0 top-full mt-1.5 z-30 w-[290px] max-w-[calc(100vw-2.25rem)] bg-white border border-[#E5E7EB] rounded-xl shadow-[0_12px_32px_rgba(17,24,39,0.14)] p-1.5 overflow-hidden"
        >
          {MENU_ITEMS.map((item, index) => (
            <React.Fragment key={item.scope}>
              {index > 0 ? (
                <div className="mx-2 my-1 h-px bg-[#F3F4F6]" aria-hidden="true" />
              ) : null}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setPendingScope(item.scope);
                  close();
                }}
                className="w-full min-h-[44px] text-left px-3 py-3 rounded-lg text-sm font-semibold text-[#DC2626] hover:bg-[#FEF2F2] break-words"
              >
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* The platform's ConfirmationModal, in its V2 layout: same component
          the rest of the platform confirms with, only with the padded overlay
          and the two 44px centred buttons this surface needs on a phone. The
          four honest lines are the same four sentences, now as `lines` instead
          of block-level spans stuffed into `message`. */}
      <ConfirmationModal
        isOpen={!!pendingScope}
        onClose={() => (banning ? null : setPendingScope(null))}
        onConfirm={handleConfirm}
        loading={banning}
        variant="v2"
        title={copy ? copy.headline : ""}
        message={null}
        lines={
          copy
            ? [copy.effect, copy.silence, copy.unaffected, copy.reversible]
            : []
        }
        confirmText="Ban agent"
        confirmButtonClass="bg-[#EF4444] hover:bg-[#dc2626]"
      />
    </div>
  );
};

export default BanAgentMenu;
